import { get } from "lodash";
import { shallowEqual, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";

import {
  filterAdminRoutesToRole,
  getAccountClosedRoutes,
  getEmployerRoutes,
  getLockedUserRoutes,
  getOnboardingEmployerRoutes,
  getResponsiveSideBarRoutes,
  individualRoutes,
} from "pages/dashboards/Routes";
import {
  employerOnboardingStates,
  IndividualClosed,
  IndividualClosePending,
} from "statics/states";
import { AGENT_ROLE_ID, EMPLOYER_ADMIN_ROLE_ID } from "statics/roles";
import { getAdminRoles } from "store/selectors/admin";
import type { RootState } from "types/state";
import {
  hasEnabledPayrollIntegration,
  hasEnabledUkgPayrollIntegration,
  hasRunFirstPayroll,
  hasStatePlanSelector,
  hasVirtualBankEnabled,
} from "store/selectors/employer";
import { userIsLockedSelector } from "store/selectors/user";

export function useDashboardRoutes({
  isAdminDashboard,
  isEmployerDashboard,
}: {
  isAdminDashboard?: boolean;
  isEmployerDashboard?: boolean;
}) {
  const isSmallScreen = useMediaQuery({ maxWidth: 767 });

  const flags = useSelector(
    (state: RootState) => ({
      userState: get(state.user, "userState.state", ""),
      userRoleId: get(state.user, "userRoleId", ""),
      companyState: state.user.companyState,
      userIsLocked: userIsLockedSelector(state),
      hasRanFirstPayroll: hasRunFirstPayroll(state),
      hasStatePlan: hasStatePlanSelector(state),
      hasPayrollIntegration: hasEnabledPayrollIntegration(state),
      hasVirtualBank: hasVirtualBankEnabled(state),
      hasUkgPayrollIntegration: hasEnabledUkgPayrollIntegration(state),
      adminRoles: getAdminRoles(state),
    }),
    shallowEqual
  );

  const accountClosed = flags.userState === IndividualClosed;
  const accountPendingClose = flags.userState === IndividualClosePending;
  const isOwner = flags.userRoleId === EMPLOYER_ADMIN_ROLE_ID;
  const isAdministrator = flags.userRoleId === AGENT_ROLE_ID;
  const isOnboardingEmployer = employerOnboardingStates.includes(
    // default to "" when there's no state — never matches an onboarding state,
    // same result as the previous falsy default, no cast needed
    get(flags.companyState, "state", "")
  );

  if (accountClosed || accountPendingClose) {
    return getAccountClosedRoutes();
  }
  if (isEmployerDashboard && isOnboardingEmployer) {
    return getOnboardingEmployerRoutes(isOwner);
  }
  if (isEmployerDashboard) {
    return getEmployerRoutes({
      // hasRunFirstPayroll selector is loosely typed (unknown); getEmployerRoutes
      // (now TS) wants boolean — cast at the boundary, value unchanged.
      hasRanFirstPayroll: flags.hasRanFirstPayroll as boolean,
      hasStatePlan: flags.hasStatePlan,
      isAdministrator,
      hasPayrollIntegration: flags.hasPayrollIntegration,
      hasVirtualBank: flags.hasVirtualBank,
      hasUkgPayrollIntegration: flags.hasUkgPayrollIntegration,
    });
  }
  if (isAdminDashboard) {
    return filterAdminRoutesToRole(flags.adminRoles);
  }
  if (flags.userIsLocked) {
    return getLockedUserRoutes();
  }
  if (isSmallScreen) {
    return getResponsiveSideBarRoutes();
  }
  return individualRoutes;
}
